export function formatShortId(value: string): string {
  const [firstPart] = value.split('-');
  return firstPart || value;
}

export function formatDateTime(value: string | null): string {
  if (!value) {
    return 'Never';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
}

export function formatPricingTier(value: string): string {
  return value.replace('_', ' ');
}

export function formatOwnerDisplayName(organization: {
  ownerName: string | null;
  ownerEmail: string | null;
}): string {
  return organization.ownerName ?? organization.ownerEmail ?? 'Unassigned';
}

export function formatBoolean(value: boolean): string {
  return value ? 'Yes' : 'No';
}

export function formatNullableValue(value: string | null): string {
  return value ?? 'Not available yet';
}

export function formatSettingLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (first) => first.toUpperCase());
}

export function formatPrimitiveSetting(value: unknown): string {
  if (value === null || value === undefined) {
    return 'Not available yet';
  }
  if (typeof value === 'boolean') {
    return formatBoolean(value);
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    return value || 'Not available yet';
  }
  return JSON.stringify(value);
}

export function formatTransactionTypeLabel(value: string): string {
  switch (value) {
    case 'purchase':
      return 'Purchase';
    case 'voucher':
      return 'Voucher';
    case 'usage':
      return 'Usage';
    case 'onboarding':
      return 'Onboarding';
    case 'adjustment':
      return 'Adjustment';
    default:
      return formatSettingLabel(value);
  }
}

export function formatTransactionReferenceSource(value: string): string {
  switch (value) {
    case 'payments-data.purchases.id':
      return 'Purchase';
    case 'interviews-data.sessions.id':
      return 'Interview session';
    case 'pricing_tier.schema':
      return 'Pricing tier';
    case 'backoffice-bff.adjustments.id':
      return 'Backoffice adjustment';
    case 'purchaser-controller.payment_sessions.id':
      return 'Checkout session';
    case 'vouchers-data.redemptions.id':
      return 'Voucher redemption';
    default:
      return formatSettingLabel(value);
  }
}

export function formatMinorAmount(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amountMinor / 100);
}

export function formatSalesSummary(
  items: Array<{ currency: string; netAmountMinor: number }>
): string {
  if (items.length === 0) {
    return 'No sales yet';
  }

  const sortedItems = [...items].sort((left, right) => {
    if (left.currency === right.currency) {
      return 0;
    }
    if (left.currency === 'USD') {
      return -1;
    }
    if (right.currency === 'USD') {
      return 1;
    }
    return left.currency.localeCompare(right.currency);
  });

  return sortedItems
    .map((item) => formatMinorAmount(item.netAmountMinor, item.currency))
    .join(' + ');
}

export function formatCardLabel(paymentMethod?: { brand: string; last4: string }): string {
  if (!paymentMethod) {
    return 'Not available';
  }

  return `${paymentMethod.brand.toUpperCase()} •••• ${paymentMethod.last4}`;
}

export function formatSalesStatusLabel(value: string): string {
  switch (value) {
    case 'completed':
      return 'Completed';
    case 'pending':
      return 'Pending';
    case 'refunded':
      return 'Refunded';
    case 'partially_refunded':
      return 'Partially refunded';
    case 'canceled':
      return 'Canceled';
    default:
      return formatSettingLabel(value);
  }
}
